import { isNativePlatform } from "@/lib/capacitor";
import { Preferences } from "@capacitor/preferences";
import {
  ACCESS_TOKEN_COOKIE_NAME,
  REFRESH_TOKEN_COOKIE_NAME,
} from "muminsmart-utils";
import type { IUser } from "muminsmart-utils";

export const AUTH_USER_STORAGE_KEY = "auth_user";

export interface StoredAuth {
  accessToken: string;
  refreshToken: string;
  user: IUser;
}

const isBrowser = () => typeof window !== "undefined";

async function setItem(key: string, value: string): Promise<void> {
  if (!isBrowser()) {
    return;
  }

  if (isNativePlatform()) {
    await Preferences.set({ key, value });
    return;
  }

  window.localStorage.setItem(key, value);
}

async function getItem(key: string): Promise<string | null> {
  if (!isBrowser()) {
    return null;
  }

  if (isNativePlatform()) {
    const { value } = await Preferences.get({ key });
    return value;
  }

  return window.localStorage.getItem(key);
}

async function removeItem(key: string): Promise<void> {
  if (!isBrowser()) {
    return;
  }

  if (isNativePlatform()) {
    await Preferences.remove({ key });
    return;
  }

  window.localStorage.removeItem(key);
}

export async function getStoredAuth(): Promise<StoredAuth | null> {
  const [accessToken, refreshToken, rawUser] = await Promise.all([
    getItem(ACCESS_TOKEN_COOKIE_NAME),
    getItem(REFRESH_TOKEN_COOKIE_NAME),
    getItem(AUTH_USER_STORAGE_KEY),
  ]);

  if (!accessToken || !refreshToken || !rawUser) {
    return null;
  }

  try {
    return {
      accessToken,
      refreshToken,
      user: JSON.parse(rawUser) as IUser,
    };
  } catch {
    return null;
  }
}

export async function setStoredAuth(auth: StoredAuth): Promise<void> {
  await Promise.all([
    setItem(ACCESS_TOKEN_COOKIE_NAME, auth.accessToken),
    setItem(REFRESH_TOKEN_COOKIE_NAME, auth.refreshToken),
    setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(auth.user)),
  ]);
}

export async function clearStoredAuth(): Promise<void> {
  await Promise.all([
    removeItem(ACCESS_TOKEN_COOKIE_NAME),
    removeItem(REFRESH_TOKEN_COOKIE_NAME),
    removeItem(AUTH_USER_STORAGE_KEY),
  ]);
}

export async function getStoredTokens(): Promise<{
  accessToken?: string;
  refreshToken?: string;
}> {
  const [accessToken, refreshToken] = await Promise.all([
    getItem(ACCESS_TOKEN_COOKIE_NAME),
    getItem(REFRESH_TOKEN_COOKIE_NAME),
  ]);

  return {
    accessToken: accessToken ?? undefined,
    refreshToken: refreshToken ?? undefined,
  };
}
